import { useQuery } from '@tanstack/react-query';
import { AxiosMethods } from '@setvi/shared/enums';
import { axiosHelper } from '@setvi/shared/services';

// v2.0 location endpoints. Hit ADMIN_API_BASE_URL (https://devapi.setvi.com in
// develop).
const LOCATION_ENDPOINT = '/api/v2.0/ShoppingCart/location';
const DYNAMIC_LOCATION_ENDPOINT = '/api/v2.0/ShoppingCart/dynamiclocation';

enum LocationQueryKey {
  Ids = 'print_order_location_ids'
}

// Location data for a resource. `LocationId` prefills the location input.
export interface LocationData {
  ResourceId?: number;
  HasLocation?: boolean;
  Available?: boolean;
  LocationId?: string | null;
  RoomID?: string | null;
  Message?: string | null;
}

interface LocationApiResponse {
  Status: string;
  Data: LocationData | null;
  Error: { Id: number; Message: string } | string | null;
}

const getLocationsQuery = (resourceId: number) => ({
  queryKey: [LocationQueryKey.Ids, resourceId],
  queryFn: async (): Promise<LocationData | null> => {
    const response: LocationApiResponse = await axiosHelper({
      endpoint: `${LOCATION_ENDPOINT}/${resourceId}`,
      method: AxiosMethods.GET,
      baseURL: process.env.ADMIN_API_BASE_URL
    });
    return response?.Data ?? null;
  }
});

/**
 * Fetch the location id for a resource. Runs only once `enabled` is true (the
 * location dialog is open), mirroring `useQrCodes`. A resource with no location
 * data returns a 4xx — a normal "nothing available" state, so we don't retry or
 * toast; `locationData` is simply null.
 */
export const useLocations = (resourceId: number, enabled: boolean) => {
  const { data, isLoading } = useQuery({
    ...getLocationsQuery(resourceId),
    enabled: enabled && !!resourceId,
    retry: false
  });
  return { locationData: data ?? null, loading: isLoading };
};

// Generated dynamic location PDF — `DownloadURLWithSas` drives the preview,
// `FileName` is the generated name. Mirrors the /dynamicqrcode shape.
export interface DynamicLocationData {
  Id: number;
  Type: number;
  TypeName: string | null;
  ResourceId: number;
  FileName: string;
  BlobPath: string | null;
  DownloadURL: string | null;
  DownloadURLWithSas: string | null;
  CoverPageName: string | null;
  MachineId: string | null;
  QrUrl: string | null;
  CreatedUtc: string | null;
}

interface DynamicLocationApiResponse {
  Status: string;
  Data: DynamicLocationData | null;
  Error: { Id: number; Message: string } | string | null;
}

export interface GenerateDynamicLocationInput {
  resourceId: number;
  locationId: string;
}

/**
 * Generate the dynamic location PDF for a resource + location id. Returns a
 * single PDF (DownloadURLWithSas) used for the preview.
 */
const generateDynamicLocation = async ({
  resourceId,
  locationId
}: GenerateDynamicLocationInput): Promise<DynamicLocationData | null> => {
  const response: DynamicLocationApiResponse = await axiosHelper({
    endpoint: DYNAMIC_LOCATION_ENDPOINT,
    method: AxiosMethods.POST,
    baseURL: process.env.ADMIN_API_BASE_URL,
    body: { resourceId, locationId }
  });
  return response?.Data ?? null;
};

// React Query mutation factory (used via useSubscribedMutation). Called on
// "Generate".
export const generateDynamicLocationMutation = () => ({
  mutationFn: generateDynamicLocation
});
